package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ChatViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.chat.ChatInfoPanel
import com.arena_web.ui.components.chat.ChatListPanel
import com.arena_web.ui.components.chat.ChatRoomPanel
import com.arena_web.ui.components.chat.NewChatDialog
import com.arena_web.ui.components.navigation.BusinessSidebarTab
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.layouts.BusinessPageLayout
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URLSearchParams
import org.w3c.files.FileReader
import org.w3c.files.get

@Page("/chat")
@Composable
fun ChatPage() {
    val searchParams = remember { URLSearchParams(window.location.search) }
    val userParam = remember { searchParams.get("user") }
    val userName = remember { searchParams.get("name") }
    val userAvatar = remember { searchParams.get("avatar") }
    ChatPageContent(targetUserId = userParam, targetUserName = userName, targetUserAvatar = userAvatar)
}

@Composable
fun ChatPageContent(initialChatId: String? = null, targetUserId: String? = null, targetUserName: String? = null, targetUserAvatar: String? = null) {
    val t = com.arena_web.ui.localization.LocalAppStrings.current
    val container = LocalAppContainer.current
    val chatRepository = container.chatRepository

    val isBusiness = remember { Storage.get(StorageKeys.ACTIVE_ROLE) == "BUSINESS" }

    // Pre-create ViewModel + state at this level so we can compute layout
    // chrome (e.g. hide the bottom nav inside the room/details) BEFORE
    // calling AppPageLayout.
    val viewModel = if (chatRepository != null) {
        rememberViewModel(key = "chat") {
            ChatViewModel(
                chatRepository = chatRepository,
                followRepository = container.followRepository,
                userWsClient = container.userWsClient
            )
        }
    } else null
    val state = viewModel?.uiState?.collectAsState()?.value
    val mobileInRoomOrDetails = isMobile() && state?.selectedChatId != null

    val chatContent: @Composable com.varabyte.kobweb.compose.foundation.layout.ColumnScope.() -> Unit = content@{
        if (chatRepository == null || viewModel == null || state == null) {
            com.arena_web.ui.sections.auth.WelcomeToArenaScreen(
                onSignedIn = { kotlinx.browser.window.location.reload() }
            )
            return@content
        }

        // Auto-select chat from the deep link — once per mount so Back
        // (which clears `selectedChatId`) doesn't re-enter the room.
        LaunchedEffect(Unit) {
            if (initialChatId != null) viewModel.selectChat(initialChatId)
        }

        // Update URL when selected chat changes
        LaunchedEffect(state.selectedChatId) {
            val chatId = state.selectedChatId
            val newPath = if (chatId != null) "/chat/$chatId" else "/chat"
            if (window.location.pathname != newPath) {
                window.history.replaceState(null, "", newPath)
            }
        }

        // Open direct chat from profile page (?user=userId)
        LaunchedEffect(targetUserId) {
            if (targetUserId != null) {
                viewModel.startDirectChatWithUser(targetUserId, targetUserName, targetUserAvatar)
            }
        }

        if (isMobile()) {
            // Switch on selectedChatId rather than waiting for the loaded
            // ChatDto — the room screen opens instantly using the cached
            // list-item snapshot for the header, while messages + detail
            // load in the background.
            val selectedId = state.selectedChatId
            var showDetails by remember(selectedId) { mutableStateOf(false) }
            if (selectedId != null) {
                val listItem = state.chats.firstOrNull { it.id == selectedId }
                if (showDetails) {
                    com.arena_web.ui.sections.chat.mobile.MobileChatDetails(
                        chat = state.selectedChat,
                        fallbackListItem = listItem,
                        onlineUserIds = state.onlineUserIds,
                        currentUserId = state.currentUserId,
                        notificationsOn = state.selectedChat?.isMuted?.not() ?: !(listItem?.isMuted ?: false),
                        onToggleNotifications = { viewModel.muteChat() },
                        onBack = { showDetails = false },
                        // Both branches close the room screen once the chat
                        // is gone — leaveChat handles delete + leave on the
                        // backend identically (see ChatViewModel).
                        onDeleteChat = {
                            viewModel.leaveChat()
                            showDetails = false
                        },
                        onLeaveChat = {
                            viewModel.leaveChat()
                            showDetails = false
                        },
                        onRemoveMember = { userId -> viewModel.requestRemoveMember(userId) },
                        onEditGroup = { viewModel.showEditChatDialog() },
                        onInviteMembers = { viewModel.showInviteDialog() },
                    )

                    // Edit-group + invite sheets layer over the details screen.
                    if (state.showEditChatDialog) {
                        com.arena_web.ui.components.overlay.BottomSheet(
                            onDismiss = { viewModel.hideEditChatDialog() }
                        ) {
                            com.arena_web.ui.components.chat.EditChatContent(
                                initialName = state.selectedChat?.name ?: listItem?.name ?: "",
                                currentImageUrl = state.selectedChat?.imageUrl ?: listItem?.imageUrl,
                                isSaving = state.savingGroup,
                                onSave = { name, bytes, fileName, contentType ->
                                    viewModel.editGroupChat(name, bytes, fileName, contentType)
                                },
                                onCancel = { viewModel.hideEditChatDialog() },
                            )
                        }
                    }
                    if (state.showInviteDialog) {
                        com.arena_web.ui.components.overlay.BottomSheet(
                            onDismiss = { viewModel.hideInviteDialog() }
                        ) {
                            com.arena_web.ui.components.chat.InviteMembersContent(
                                searchQuery = state.followingSearchQuery,
                                onSearchChanged = { viewModel.setFollowingSearchQuery(it) },
                                results = state.following,
                                isLoading = state.followingLoading,
                                isLoadingMore = state.followingLoadingMore,
                                hasMore = state.followingHasMore,
                                isInviting = state.invitingMembers,
                                onLoadMore = { viewModel.loadMoreFollowing() },
                                onInvite = { ids -> viewModel.inviteMembers(ids) },
                                onCancel = { viewModel.hideInviteDialog() },
                            )
                        }
                    }
                    state.memberPendingRemoval?.let { member ->
                        com.arena_web.ui.components.overlay.DesktopPopup(
                            onDismiss = { viewModel.cancelRemoveMember() },
                            widthPx = 320,
                            mobile = true,
                        ) {
                            com.arena_web.ui.components.chat.RemoveMemberConfirmContent(
                                member = member,
                                onConfirm = { viewModel.confirmRemoveMember() },
                                onCancel = { viewModel.cancelRemoveMember() },
                            )
                        }
                    }
                    return@content
                }
                com.arena_web.ui.sections.chat.mobile.MobileChatRoom(
                    chat = state.selectedChat,
                    fallbackListItem = listItem,
                    isLoading = state.messagesLoading,
                    messages = state.messages,
                    messageText = state.messageText,
                    onlineUserIds = state.onlineUserIds,
                    currentUserId = state.currentUserId,
                    onBack = { viewModel.closeSelectedChat() },
                    onAvatarClick = { showDetails = true },
                    onTextChanged = { viewModel.updateMessageText(it) },
                    onSend = { viewModel.sendMessage() },
                    onTyping = { viewModel.sendTyping() },
                )
            } else {
                com.arena_web.ui.sections.chat.mobile.MobileChatList(
                    chats = state.chats,
                    searchQuery = state.chatsSearchQuery,
                    onSearchQueryChanged = { viewModel.setChatsSearchQuery(it) },
                    onChatSelected = { chatId -> viewModel.selectChat(chatId) },
                    onNewChatClick = { viewModel.showNewChatDialog() },
                )

                // The desktop NewChatDialog below is never reached on mobile
                // (we return early here) — render the bottom-sheet variant instead.
                if (state.showNewChatDialog) {
                    com.arena_web.ui.sections.chat.mobile.MobileNewChatSheet(
                        following = state.following,
                        isLoading = state.followingLoading,
                        isLoadingMore = state.followingLoadingMore,
                        hasMore = state.followingHasMore,
                        searchQuery = state.followingSearchQuery,
                        isCreating = state.creatingChat,
                        onSearchQueryChanged = { viewModel.setFollowingSearchQuery(it) },
                        onCreateDirect = { userId -> viewModel.createDirectChat(userId) },
                        onCreateGroup = { name, imageBytes, imageFileName, imageContentType ->
                            viewModel.createGroupChat(name, imageBytes = imageBytes, imageFileName = imageFileName, imageContentType = imageContentType)
                        },
                        onLoadMore = { viewModel.loadMoreFollowing() },
                        onDismiss = { viewModel.hideNewChatDialog() }
                    )
                }
            }
            return@content
        }

        Row(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(26, 40)
                .sGap(0)
                .styleModifier {
                    property("min-height", "0")
                }
        ) {
            // Left: Chat list
            ChatListPanel(
                chats = state.chats,
                selectedChatId = state.selectedChatId,
                onlineUserIds = state.onlineUserIds,
                isLoading = state.chatsLoading,
                searchQuery = state.chatsSearchQuery,
                onSearchQueryChanged = { viewModel.setChatsSearchQuery(it) },
                isLoadingMore = state.chatsLoadingMore,
                hasMore = state.chatsHasMore,
                onLoadMore = { viewModel.loadMoreChats() },
                onChatSelected = { chatId -> viewModel.selectChat(chatId) },
                onNewChatClick = { viewModel.showNewChatDialog() },
                modifier = Modifier.sWidth(360)
            )

            // Right: Chat room + info panel
            ChatRoomPanel(
                chat = state.selectedChat,
                messages = state.messages,
                messageText = state.messageText,
                typingUsers = state.typingUsers,
                onlineUserIds = state.onlineUserIds,
                currentUserId = state.currentUserId,
                isLoading = state.messagesLoading,
                loadingMore = state.loadingMore,
                hasMoreMessages = state.hasMoreMessages,
                editingMessage = state.editingMessage,
                showMessageSearch = state.showMessageSearch,
                messageSearchQuery = state.messageSearchQuery,
                messageSearchResults = state.messageSearchResults,
                showChatMenu = state.showChatMenu,
                pinnedMessage = state.pinnedMessage,
                replyingToMessage = state.replyingToMessage,
                pendingFile = state.pendingFile,
                uploadingFile = state.uploadingFile,
                onTextChanged = { viewModel.updateMessageText(it) },
                onSend = { viewModel.sendMessage() },
                onTyping = { viewModel.sendTyping() },
                onLoadMore = { viewModel.loadMoreMessages() },
                onDeleteMessage = { viewModel.deleteMessage(it) },
                onEditMessage = { viewModel.startEditMessage(it) },
                onCancelEdit = { viewModel.cancelEditMessage() },
                onSubmitEdit = { viewModel.submitEditMessage() },
                onSearchClick = { viewModel.toggleMessageSearch() },
                onSearchQueryChanged = { viewModel.updateSearchQuery(it) },
                onMenuClick = { viewModel.toggleChatMenu() },
                onMuteChat = { viewModel.muteChat() },
                onLeaveChat = { viewModel.leaveChat() },
                onDismissMenu = { viewModel.closeChatMenu() },
                onReaction = { messageId, emoji -> viewModel.toggleReaction(messageId, emoji) },
                onReplyMessage = { viewModel.startReply(it) },
                onCancelReply = { viewModel.cancelReply() },
                onPinMessage = { viewModel.pinMessage(it) },
                onAttachmentClick = {
                    pickFile { bytes, name, contentType ->
                        viewModel.setPendingFile(bytes, name, contentType)
                    }
                },
                onSendFile = { viewModel.sendMediaMessage() },
                onCancelFile = { viewModel.clearPendingFile() },
                onInfoClick = { viewModel.toggleChatInfo() },
                showInfoPanel = state.showChatInfo && state.selectedChat != null,
                chatsLoading = state.chatsLoading,
                modifier = Modifier.styleModifier { property("flex", "1") }
            )

            // Chat info panel with slide animation
            val showInfo = state.showChatInfo && state.selectedChat != null
            var infoVisible by remember { mutableStateOf(false) }

            LaunchedEffect(showInfo) {
                infoVisible = showInfo
            }

            if (showInfo || infoVisible) {
                ChatInfoPanel(
                    chat = state.selectedChat!!,
                    onlineUserIds = state.onlineUserIds,
                    currentUserId = state.currentUserId,
                    onClose = { viewModel.closeChatInfo() },
                    onMuteChat = { viewModel.muteChat() },
                    onLeaveChat = { viewModel.leaveChat() },
                    onDeleteChat = { viewModel.leaveChat() },
                    onFetchMessages = { cId, params -> chatRepository.getMessages(cId, params) },
                    onEditGroup = { viewModel.showEditChatDialog() },
                    onInviteMembers = { viewModel.showInviteDialog() },
                    onRemoveMember = { userId -> viewModel.requestRemoveMember(userId) },
                    modifier = Modifier.styleModifier {
                        property("transition", "max-width 0.3s ease, opacity 0.3s ease")
                        property("max-width", if (infoVisible && showInfo) s(360) else "0px")
                        property("opacity", if (infoVisible && showInfo) "1" else "0")
                        property("overflow", "hidden")
                    }
                )
            }
        }

        // New chat dialog overlay
        if (state.showNewChatDialog) {
            NewChatDialog(
                following = state.following,
                isLoading = state.followingLoading,
                isLoadingMore = state.followingLoadingMore,
                hasMore = state.followingHasMore,
                searchQuery = state.followingSearchQuery,
                isCreating = state.creatingChat,
                onSearchQueryChanged = { viewModel.setFollowingSearchQuery(it) },
                onCreateDirect = { userId -> viewModel.createDirectChat(userId) },
                onCreateGroup = { name, imageBytes, imageFileName, imageContentType ->
                    viewModel.createGroupChat(name, imageBytes = imageBytes, imageFileName = imageFileName, imageContentType = imageContentType)
                },
                onLoadMore = { viewModel.loadMoreFollowing() },
                onDismiss = { viewModel.hideNewChatDialog() }
            )
        }

        // Edit-group + invite popups (desktop). Triggered from the chat info
        // panel's avatar pencil / "Invite members" row.
        if (state.showEditChatDialog) {
            com.arena_web.ui.components.overlay.DesktopPopup(
                onDismiss = { viewModel.hideEditChatDialog() }
            ) {
                com.arena_web.ui.components.chat.EditChatContent(
                    initialName = state.selectedChat?.name ?: "",
                    currentImageUrl = state.selectedChat?.imageUrl,
                    isSaving = state.savingGroup,
                    onSave = { name, bytes, fileName, contentType ->
                        viewModel.editGroupChat(name, bytes, fileName, contentType)
                    },
                    onCancel = { viewModel.hideEditChatDialog() },
                )
            }
        }
        if (state.showInviteDialog) {
            com.arena_web.ui.components.overlay.DesktopPopup(
                onDismiss = { viewModel.hideInviteDialog() }
            ) {
                com.arena_web.ui.components.chat.InviteMembersContent(
                    searchQuery = state.followingSearchQuery,
                    onSearchChanged = { viewModel.setFollowingSearchQuery(it) },
                    results = state.following,
                    isLoading = state.followingLoading,
                    isLoadingMore = state.followingLoadingMore,
                    hasMore = state.followingHasMore,
                    isInviting = state.invitingMembers,
                    onLoadMore = { viewModel.loadMoreFollowing() },
                    onInvite = { ids -> viewModel.inviteMembers(ids) },
                    onCancel = { viewModel.hideInviteDialog() },
                )
            }
        }

        // Remove-member confirmation (desktop). Same centred popup, full scale.
        state.memberPendingRemoval?.let { member ->
            com.arena_web.ui.components.overlay.DesktopPopup(
                onDismiss = { viewModel.cancelRemoveMember() },
                widthPx = 320,
            ) {
                com.arena_web.ui.components.chat.RemoveMemberConfirmContent(
                    member = member,
                    onConfirm = { viewModel.confirmRemoveMember() },
                    onCancel = { viewModel.cancelRemoveMember() },
                )
            }
        }
    }

    if (isBusiness) {
        BusinessPageLayout(activeTab = BusinessSidebarTab.Chat, content = chatContent)
    } else {
        AppPageLayout(
            // Highlights the desktop SidebarNav tab; the mobile BottomNavBar
            // has no Chat entry (phones enter via the Arena top-bar icon), so
            // nothing lights up there.
            activeTab = SidebarTab.Chat,
            mobileReady = true,
            mobilePageTitle = t.pageChatTitle,
            // The chat list is a drill-in surface now: back arrow instead of
            // the brand logo on the left of the mobile top bar.
            mobileHeaderBack = true,
            // Hide the global top bar while inside a conversation (or its
            // details) — the room/details screens own the full surface with
            // their own header. The bottom nav is hidden on the whole chat
            // surface: it's a drill-in page entered from the top bar, not a
            // tab destination.
            mobileHideTopBar = mobileInRoomOrDetails,
            mobileHideBottomNav = true,
            content = chatContent,
        )
    }
}

private const val MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB

private fun pickFile(onFilePicked: (ByteArray, String, String) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*,.svg,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
    input.onchange = { _ ->
        val file = input.files?.get(0)
        if (file != null) {
            if (file.size.toInt() > MAX_FILE_SIZE_BYTES) {
                kotlinx.browser.window.alert("File is too large. Maximum size is 50 MB.")
            } else {
                val reader = FileReader()
                reader.onload = {
                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                    val bytes = int8Array.unsafeCast<ByteArray>()
                    // Images are downscaled/recompressed before upload (videos,
                    // audio and documents pass through untouched) — a raw camera
                    // shot on a weak mobile uplink is what aborts mid-transfer.
                    com.arena_web.utils.compressForUploadThen(bytes, file.name, file.type, onFilePicked)
                }
                reader.readAsArrayBuffer(file)
            }
        }
    }
    input.click()
}
